import {apiActionImproved} from "@/api.ts";
import AdSensePush from "@/components/AdSensePush.tsx";
import EventAuthModal from "@/components/EventAuthModal.tsx";
import FinishPlayerProfileModal, {needsPlayerProfile} from "@/components/registration/FinishPlayerProfileModal.tsx";
import LandingNav from "@/landing/components/LandingNav.tsx";
import {config} from "@/config/config.ts";
import services from "@/services";
import {useJwtAuth, useJwtUser} from "@/JWTAuthProvider.tsx";
import VerifyPhoneModal from "@/signup/VerifyPhoneModal.tsx";
import PPhoneInput, {DialCodes, toE164} from "@/components/PPhoneInput.tsx";
import {Checkbox, Input, Modal, ModalBody, ModalContent, ModalHeader, Select, SelectItem, Spinner} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useNavigate, useParams} from "@tanstack/react-router";
import {Check, ScrollText} from "lucide-react";
import {useEffect, useMemo, useRef, useState} from "react";
import {parseSquad, registrationShape, rosterSummary, teamNameProblem, TeamSportEntryCard} from "../registration/teamSportEntry";
import {signUpResultMessage} from '../components/events/registrationState.tsx';

export const Route = createFileRoute('/e/$uid_/register')({
    component: RouteComponent,
})

type DivisionWithTeams = {
    key: string;
    name: string;
    entry_amount: number | null;
    capacity: number | null;
    count: number;
    teams: {name: string; players: string[]}[];
};

// A partner is either an existing player (uid from search) or a guest added by name (no uid).
// partnerMode: 'partner' = pick a partner now; 'later' = register solo and add a partner later
// (application with no partner); 'looking' = register solo AND join the event's partner-search list.
type PartnerMode = 'partner' | 'later' | 'looking';
type TeamEntry = {teamName: string; squad?: string; partnerMode?: PartnerMode; partner: {uid?: string; name: string; email?: string; phone?: string; image?: string} | null};

// Small avatar for partner search results / selected partner — profile image, or initial fallback.
function PartnerAvatar({name, image}: {name: string; image?: string}) {
    if (image) {
        return <img src={image} alt={name} className="h-6 w-6 shrink-0 rounded-full object-cover" />;
    }
    return (
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#082F8A]/10 text-[11px] font-bold text-[#082F8A]">
            {(name || '?').charAt(0).toUpperCase()}
        </span>
    );
}

// Best-effort split of a stored E.164 number back into {country, national} so an existing number
// pre-fills the picker. Prefers the user's own country dial code, else the longest matching prefix.
function splitE164(e164: string | null | undefined, fallbackCountry: string): {country: string; national: string} {
    const digits = String(e164 || '').replace(/[^\d]/g, '');
    if (!digits) return {country: fallbackCountry, national: ''};
    const fbDial = DialCodes[fallbackCountry];
    if (fbDial && digits.startsWith(fbDial)) return {country: fallbackCountry, national: digits.slice(fbDial.length)};
    const entry = Object.entries(DialCodes).sort((a, b) => b[1].length - a[1].length).find(([, d]) => digits.startsWith(d));
    if (entry) return {country: entry[0], national: digits.slice(entry[1].length)};
    return {country: fallbackCountry, national: digits};
}

function RouteComponent() {
    const {uid} = useParams({from: '/e/$uid_/register'});
    const user = useJwtUser();
    const jwt = useJwtAuth();
    const navigate = useNavigate();
    const [authOpen, setAuthOpen] = useState(true);

    // Let a signed-in user register under a different account — e.g. an organizer who needs to enter
    // as a player. Signing out flips `user` to null, which re-opens the auth modal to log in / sign up.
    const switchAccount = async () => {
        setAuthOpen(true);
        await jwt.signOut();
    };
    const [step, setStep] = useState<'divisions' | 'teams' | 'information' | 'checkout'>('divisions');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [teams, setTeams] = useState<Record<string, TeamEntry>>({});
    const [formAnswers, setFormAnswers] = useState<Record<string, string>>({});
    const [waiversAccepted, setWaiversAccepted] = useState<Record<string, boolean>>({});
    const [contacts, setContacts] = useState<Record<string, {email?: string}>>({});
    // Phone captured per registrant with an explicit country-code picker, stored as {country, national}.
    const [phoneInputs, setPhoneInputs] = useState<Record<string, {country: string; national: string}>>({});
    const [openWaiver, setOpenWaiver] = useState<any | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // Progressive, non-blocking phone-verify prompt shown after a successful (free / pay-later)
    // registration when the registrant hasn't verified a mobile yet. Navigation to the event page
    // is deferred until the prompt closes.
    const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);

    const eventQuery = useQuery({
        queryKey: ['public-event', uid],
        queryFn:  () => services.publicEvent.show(uid),
        enabled:  !!uid,
    });
    const e = eventQuery.data;

    // Singles tournaments (participants_type 3) skip the team step; doubles (1/2) need a partner.
    // Leagues with INDIVIDUAL ranking (DoublesIndividual, 1) also skip it: pairs are formed per
    // game there, so each player registers alone — there is no team to select.
    // A team sport (football, basketball, …) registers a named TEAM — captain + optional squad, no
    // partner — so it is never "doubles"; it still has the team step, to name the team.
    const {teamSport, isDoubles} = registrationShape(e);
    const hasTeamStep = isDoubles || teamSport;
    // Per-player entry fee: each player pays their own, so we capture the partner's contact to send
    // them their own payment link.
    const perPlayer = isDoubles && e?.entry_fee_basis === 'per_player';
    // The registrant already has an entry for this event (e.g. returning to add a partner). In that
    // case they've already paid their share, so we skip the payment checkout and just submit.
    const alreadyRegistered = !!e?.my_application;
    // A returning doubles entrant who registered solo ("choose later") coming back just to add their
    // partner. We pre-select their division, jump straight to the partner step, skip the questions
    // they already answered, and never re-charge them.
    const addPartnerMode = alreadyRegistered && isDoubles && !e?.my_application?.partner_player_uid;

    const divisions: DivisionWithTeams[] = useMemo(() => {
        const real = (e?.divisions_with_teams as DivisionWithTeams[]) || [];
        if (e && real.length === 0) {
            // No configured divisions → register a single whole-event entry via a virtual division.
            return [{
                key:          '__event__',
                name:         'Registration',
                entry_amount: e.entry_amount ?? null,
                capacity:     e.max_participants ?? null,
                count:        e.approved_count ?? 0,
                teams:        [],
            }];
        }
        return real;
    }, [e]);
    const noDivisions = divisions.length === 1 && divisions[0].key === '__event__';
    const selectedDivisions = divisions.filter((d) => selected.has(d.key));

    // Event-level fullness — mirrors the backend WaitlistService: new entries on a full event are
    // created as Waitlisted, so registering becomes an explicit "join the waitlist" opt-in.
    const eventFull = e?.max_participants != null && (e?.approved_count ?? 0) >= e.max_participants;

    // Pickleball events surface the registrant's DUPR standing (Verified / DUPR+) on the form.
    const isPickleball = (e?.sport_type || '').toLowerCase().includes('pick');

    // Auto-select the single virtual entry so no-division events go straight through the wizard —
    // but never auto-opt someone into a waitlist: on a full event they must tick it themselves.
    useEffect(() => {
        if (noDivisions && !eventFull) setSelected((prev) => (prev.size ? prev : new Set(['__event__'])));
    }, [noDivisions, eventFull]);

    // Returning to add a partner: pre-select the division they already entered and jump straight to
    // the partner step (no division picking, no questions, no payment).
    const addPartnerInit = useRef(false);
    useEffect(() => {
        if (!addPartnerMode || addPartnerInit.current) return;
        const divKey = noDivisions ? '__event__' : (e?.my_application?.division || null);
        if (!divKey) return;
        addPartnerInit.current = true;
        setSelected(new Set([divKey]));
        setStep('teams');
    }, [addPartnerMode, noDivisions, e]);

    // Organizer-configured questions + waivers (the final "Information" step).
    const fields: any[] = e?.registration_fields || [];
    const waiversList: any[] = e?.waivers || [];
    // The organizer opts in to collecting a telephone number (Setup → Registration toggle).
    const collectPhone = !!e?.collect_phone;

    // Phone picker state per registrant. The number is stored split (country + national) and combined
    // to E.164 on submit; an existing stored number pre-fills it.
    const defaultPhoneCountry = ((user as any)?.country_code && DialCodes[String((user as any).country_code).toUpperCase()])
        ? String((user as any).country_code).toUpperCase()
        : 'GB';
    const getPhone = (key: string, self?: boolean) =>
        phoneInputs[key] ?? (self && (user as any)?.phone_number
            ? splitE164((user as any).phone_number, defaultPhoneCountry)
            : {country: defaultPhoneCountry, national: ''});
    const setPhone = (key: string, patch: Partial<{country: string; national: string}>, self?: boolean) =>
        setPhoneInputs((prev) => ({...prev, [key]: {...getPhone(key, self), ...patch}}));
    // When the organizer collects phone, the registrant's own mobile is required (country code +
    // at least a few digits). When they don't, there's nothing to require.
    const selfPhoneNational = user?.uid ? getPhone(user.uid, true).national.replace(/\D/g, '') : '';
    const phoneRequiredOk = !collectPhone || (!!user?.uid && selfPhoneNational.length >= 5);

    // The info step shows when there are organizer questions/waivers, a doubles contact step, or a
    // required phone to collect.
    const hasInfo = fields.length > 0 || waiversList.length > 0 || isDoubles || (!!user && collectPhone);
    const infoValid =
        phoneRequiredOk &&
        fields.every((f) => !f.required || (formAnswers[f.key] || '').trim() !== '') &&
        waiversList.every((w) => waiversAccepted[w.key]);

    // The registrant plus each partner across the selected divisions. Existing players (uid) and
    // guest partners (added by name) both get a card; 'later'/'looking' divisions add no partner.
    type RegPlayer = {key: string; uid?: string; name: string; self?: boolean; image?: string; isGuest?: boolean; divKey?: string};
    const regPlayers: RegPlayer[] = [];
    if (user?.uid) regPlayers.push({key: user.uid, uid: user.uid, name: user.display_name || user.name || 'You', self: true, image: (user as any).profile_thumb_url || (user as any).profile_image_url || undefined});
    {
        const seen = new Set(user?.uid ? [user.uid] : []);
        selectedDivisions.forEach((d) => {
            const t = teams[d.key];
            if ((t?.partnerMode || 'partner') !== 'partner') return; // no partner for 'later'/'looking'
            const p = t?.partner;
            if (!p) return;
            if (p.uid) {
                if (!seen.has(p.uid)) {
                    seen.add(p.uid);
                    regPlayers.push({key: p.uid, uid: p.uid, name: p.name, image: p.image});
                }
            } else {
                // Guest partner: created server-side at submit; the email here flows via partner_email.
                regPlayers.push({key: `guest:${d.key}`, name: p.name, image: p.image, isGuest: true, divKey: d.key});
            }
        });
    }
    const cEmail = (p: RegPlayer) => p.isGuest
        ? (teams[p.divKey!]?.partner?.email || '')
        : (contacts[p.key]?.email ?? (p.self ? (user?.email || '') : ''));
    const cPhone = (p: RegPlayer) => {
        const ph = getPhone(p.key, p.self);
        return toE164(ph.country, ph.national);
    };
    const setContact = (key: string, field: 'email', v: string) =>
        setContacts((prev) => ({...prev, [key]: {...prev[key], [field]: v}}));
    const setGuestEmail = (divKey: string, v: string) =>
        setTeams((prev) => {
            const t = prev[divKey];
            if (!t?.partner) return prev;
            return {...prev, [divKey]: {...t, partner: {...t.partner, email: v}}};
        });

    const currency = e?.entry_currency_code || 'USD';
    const fmt = (cents: number | null | undefined) => {
        const v = (cents || 0) / 100;
        try {
            return new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(v);
        } catch {
            return `${currency} ${v.toFixed(2)}`;
        }
    };

    // Number() matters: league entry_amount arrives as a decimal STRING ("30000.00"), and
    // `sum + "30000.00"` concatenates — the fee lines multiplied (coercing) so they looked right
    // while the payable total silently dropped the fees.
    const totalCents = selectedDivisions.reduce((sum, d) => sum + Number(d.entry_amount || 0), 0);

    // Platform fee breakdown. The organizer chooses who funds which part of the fee at checkout
    // (split_mode): absorb = they pay everything, pass = the registrant pays everything, or a
    // SPLIT — the registrant pays only the fixed part (split_fixed) or only the % (split_percent).
    // Mirrors StripeController::processEnrollDivisionsPayment, which builds the real charge.
    const feeInfo = e?.registration_fee;
    const feeRate = feeInfo?.rate ?? 0;
    const feeFixed = feeInfo?.fixed_cents ?? 0;
    const playersPerTeam = feeInfo?.players_per_team ?? (isDoubles ? 2 : 1);
    const processingFeeCents = Math.round((totalCents * feeRate) / 100);
    const fixedFeeCents = feeFixed * playersPerTeam * selected.size;
    const splitMode: string = feeInfo?.split_mode ?? (feeInfo?.pass_to_registrant ? 'pass' : 'absorb');
    const registrantPaysPercent = splitMode === 'pass' || splitMode === 'split_percent';
    const registrantPaysFixed = splitMode === 'pass' || splitMode === 'split_fixed';
    const feesTotalCents = (registrantPaysPercent ? processingFeeCents : 0) + (registrantPaysFixed ? fixedFeeCents : 0);
    const passFees = feesTotalCents > 0;
    const payableTotalCents = totalCents + (passFees && totalCents > 0 ? feesTotalCents : 0);

    // Organizers can restrict entrants to a single division.
    const allowMultiple = e?.divisions_allow_multiple ?? true;
    const toggle = (key: string) => {
        setSelected((prev) => {
            if (!allowMultiple) {
                // Single-division: selecting replaces; clicking the selected one clears it.
                return prev.has(key) ? new Set() : new Set([key]);
            }
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const setTeam = (key: string, v: TeamEntry) => setTeams((prev) => ({...prev, [key]: v}));

    const goToEvent = () => navigate({to: '/e/$uid/', params: {uid}});

    // When the organizer collects a phone, verification is a GATE: the OTP modal opens before the
    // registration is submitted, and only a successful verify proceeds to enrol. These refs carry
    // the outcome across the modal's onVerified→onClose sequence and the submit retry.
    const [preVerifyOpen, setPreVerifyOpen] = useState(false);
    const preVerifySucceeded = useRef(false);
    const phoneJustVerified = useRef(false);

    const needsPhoneVerifyNow = () => {
        if (!user || !collectPhone || phoneJustVerified.current) return false;
        const u = user as any;
        const entered = user.uid ? cPhone({key: user.uid, name: '', self: true} as any) : '';
        return entered ? !(u.phone_verified_at && u.phone_number === entered) : !u.phone_verified_at;
    };

    // On successful registration, nudge players without a verified mobile to add one (so they can
    // report scores by WhatsApp) before bouncing back to the event page. Skippable; existing
    // accounts already have a verified number, so they go straight through.
    const finishRegistration = () => {
        // Keep the "just add a partner" return flow frictionless — no phone-verify nudge.
        if (addPartnerMode || !user) { goToEvent(); return; }
        // Verified as the pre-submit gate this session — don't prompt again.
        if (phoneJustVerified.current) { goToEvent(); return; }

        const u = user as any;
        let needsVerify: boolean;
        if (collectPhone) {
            // When the organizer collects a phone, verify the number the registrant entered — even for
            // grandfathered accounts — unless it already matches this account's verified number.
            // If the entered number didn't compose to E.164, still fall back to the account gate.
            const entered = user.uid ? cPhone({key: user.uid, name: '', self: true} as any) : '';
            needsVerify = entered
                ? !(u.phone_verified_at && u.phone_number === entered)
                : !u.phone_verified_at;
        } else {
            // No phone collected for this event: fall back to the one-time account verification gate.
            needsVerify = !u.phone_verified_at;
        }

        if (needsVerify) {
            setPhoneVerifyOpen(true);
            return;
        }
        goToEvent();
    };

    const submit = async () => {
        if (needsPlayerProfile(user)) {
            return; // the minimal profile is on screen first (FinishPlayerProfileModal)
        }
        // Organizer collects phone → verify BEFORE enrolling. The modal's onClose resumes submit()
        // when verification succeeded; otherwise nothing is registered.
        if (!addPartnerMode && needsPhoneVerifyNow()) {
            preVerifySucceeded.current = false;
            setPreVerifyOpen(true);
            return;
        }

        setSubmitting(true);
        try {
            const res = await services.publicEvent.applyDivisions({
                ...(e.type === 'league' ? {league_uid: e.uid} : e.type === 'race' ? {race_uid: e.uid} : {tournament_uid: e.uid}),
                divisions:      Array.from(selected).map((key) => {
                    const t = teams[key];
                    // Only attach a partner when the registrant picked one; 'later'/'looking' register solo.
                    const p = (t?.partnerMode || 'partner') === 'partner' ? t?.partner : null;
                    return {
                        division_key:  key,
                        team_name:     t?.teamName?.trim() || undefined,
                        squad:         teamSport ? (parseSquad(t?.squad).length ? parseSquad(t?.squad) : undefined) : undefined,
                        partner_uid:   p?.uid || undefined,
                        // Guest partner added by name (no account yet) — created server-side.
                        partner_name:  p && !p.uid ? p.name : undefined,
                        // Partner contact: always sent when present (per-player needs it to send their link).
                        partner_email: p ? (p.email || undefined) : undefined,
                        partner_phone: p ? (p.phone || undefined) : undefined,
                    };
                }),
                ...(fields.length ? {form_answers: formAnswers} : {}),
                ...(waiversList.length ? {waivers_accepted: waiversList.map((w) => w.key).filter((k) => waiversAccepted[k])} : {}),
                players: regPlayers
                    .filter((p) => p.uid)
                    .map((p) => ({uid: p.uid!, email: cEmail(p).trim() || undefined, phone: cPhone(p).trim() || undefined}))
                    .filter((p) => p.email || p.phone),
            });
            const uids = res.applications.map((a) => a.application_uid);

            // If the registrant chose "Find me a partner" in any division, add them to the event's
            // partner-search list (event-level, matching the status used on the public event page).
            const wantsPartnerSearch = Array.from(selected).some((key) => (teams[key]?.partnerMode || 'partner') === 'looking');
            if (wantsPartnerSearch) {
                try {
                    await services.publicEvent.joinPartnerSearch(e.type === 'league' ? {league_uid: e.uid} : {tournament_uid: e.uid}); // races are singles — never reached
                } catch { /* non-blocking — registration already succeeded */ }
            }

            // Free entries (or organizer not payout-ready) → register now, pay later.
            // Returning to add a partner (already registered) → nothing to pay here; confirm and exit.
            if (res.total_cents <= 0 || !e.payments_ready) {
                addToast({
                    color: 'success',
                    title: alreadyRegistered
                        ? (perPlayer
                            ? "Partner added — we've emailed them their entry-fee payment link."
                            : 'Partner added to your team.')
                        : eventFull
                            ? "You're on the waiting list! The organizer will move you in if a spot opens up."
                            : signUpResultMessage(res, res.total_cents > 0),
                });
                finishRegistration();
                return;
            }

            const pay = await apiActionImproved('stripe-prepare-enroll-divisions-payment' as any, {application_uids: uids});
            if (pay.redirect_url) {
                document.location.href = pay.redirect_url;
                return;
            }
            addToast({color: 'danger', title: pay.error || 'Could not start checkout. Please try again.'});
        } catch (err: any) {
            addToast({color: 'danger', title: err?.message || 'Registration failed. Please try again.'});
        } finally {
            setSubmitting(false);
        }
    };

    // After the team/info steps: returning entrants (already paid) submit directly — no checkout.
    const proceedToCheckout = () => (alreadyRegistered ? submit() : setStep('checkout'));

    const onContinue = () => {
        if (selected.size === 0) {
            addToast({color: 'warning', title: 'Select at least one division'});
            return;
        }
        if (hasTeamStep) {
            setStep('teams');
            return;
        }
        hasInfo ? setStep('information') : proceedToCheckout();
    };

    const onSubmitTeams = () => {
        if (teamSport) {
            const problem = teamNameProblem(selectedDivisions, teams);
            if (problem) {
                addToast({color: 'warning', title: problem});
                return;
            }
            hasInfo ? setStep('information') : proceedToCheckout();
            return;
        }
        const missing = selectedDivisions.find((d) => {
            const t = teams[d.key];
            return (t?.partnerMode || 'partner') === 'partner' && !t?.partner;
        });
        if (missing) {
            addToast({color: 'warning', title: `Pick a partner for ${missing.name}, or choose "Choose later" / "Find me a partner"`});
            return;
        }
        if (perPlayer) {
            const missingContact = selectedDivisions.find((d) => {
                const t = teams[d.key];
                return (t?.partnerMode || 'partner') === 'partner' && t?.partner
                    && (!t.partner.email?.trim() || !t.partner.phone?.trim());
            });
            if (missingContact) {
                addToast({color: 'warning', title: `Add your partner's email and phone for ${missingContact.name} — we'll send them their entry-fee payment link`});
                return;
            }
        }
        // Returning to add a partner: skip the questions/waivers/phone they already answered and
        // submit straight away (proceedToCheckout submits directly for an existing entry — no payment).
        (hasInfo && !addPartnerMode) ? setStep('information') : proceedToCheckout();
    };

    const onSubmitInfo = () => {
        if (!phoneRequiredOk) {
            addToast({color: 'warning', title: 'Please enter your mobile number'});
            return;
        }
        if (!infoValid) {
            addToast({color: 'warning', title: 'Please answer all required questions and accept the waivers'});
            return;
        }
        proceedToCheckout();
    };

    return (
        <main className="min-h-screen bg-[#06133e]">
            <LandingNav />

            {!user && (
                <EventAuthModal
                    open={authOpen}
                    onClose={() => {
                        setAuthOpen(false);
                        navigate({to: '/e/$uid/', params: {uid}});
                    }}
                    onAuthenticated={() => setAuthOpen(false)}
                    eventTitle={e?.title}
                    uid={uid}
                    action="register"
                />
            )}

            {/* A new Google account (EmptyAfterSignup) finishes a minimal profile before it can enter. */}
            {needsPlayerProfile(user) && (
                <FinishPlayerProfileModal
                    open
                    sport={e?.sport_type}
                    onClose={() => navigate({to: '/e/$uid/', params: {uid}})}
                    onDone={() => { /* the reloaded user is a player: this closes and the form is theirs */ }}
                />
            )}

            {!e ? (
                <div className="flex justify-center py-24"><Spinner size="lg" /></div>
            ) : (
                <div className="mx-auto flex w-11/12 max-w-7xl gap-6 py-8">
                  <div className="min-w-0 flex-1">
                    {/* Event header */}
                    <div className="mb-6 rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-sm">
                        {e.sport_type && (
                            <span className="inline-block rounded-full border border-[#082F8A]/30 px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-[#082F8A]">
                                {e.sport_type}
                            </span>
                        )}
                        <h1 className="mt-2 font-bebas text-3xl tracking-wide text-[#1a1a2e]">{e.title}</h1>
                        {e.starts_at && (
                            <p className="text-sm text-default-500">
                                {new Date(e.starts_at).toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'})}
                                {e.ends_at ? ` — ${new Date(e.ends_at).toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'})}` : ''}
                            </p>
                        )}
                        {e.rules && (
                            <button
                                type="button"
                                onClick={() => setRulesOpen(true)}
                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#082F8A] hover:underline"
                            >
                                <ScrollText className="h-4 w-4" /> View rules
                            </button>
                        )}
                    </div>

                    {/* Who you're registering as — with an escape hatch to a different account
                        (e.g. an organizer who needs to enter the event as a player). */}
                    {user && (
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[#e5e7eb] bg-white px-5 py-3 shadow-sm">
                            <span className="flex flex-wrap items-center gap-2 text-sm text-default-600">
                                <span>
                                    Registering as <span className="font-semibold text-[#1a1a2e]">{user.display_name || user.name || 'your account'}</span>
                                    {user.email ? <span className="text-default-400"> ({user.email})</span> : null}
                                </span>
                                {/* Pickleball: the registrant's DUPR standing at a glance. */}
                                {isPickleball && (user as any).dupr_id && (
                                    (user as any).dupr_verified ? (
                                        <span title="Identity verified on DUPR"
                                              className="rounded-full bg-success-100 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-success-700">
                                            ✓ DUPR Verified
                                        </span>
                                    ) : (
                                        <span title="DUPR account connected"
                                              className="rounded-full bg-default-100 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-default-600">
                                            DUPR Connected
                                        </span>
                                    )
                                )}
                                {isPickleball && (user as any).dupr_premium && (
                                    <span title="DUPR Premium (DUPR+) member"
                                          className="rounded-full bg-[#F2E02D] px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-wide text-[#06133e]">
                                        ★ DUPR+
                                    </span>
                                )}
                            </span>
                            <button
                                type="button"
                                onClick={switchAccount}
                                className="text-sm font-semibold text-[#082F8A] underline-offset-2 hover:underline"
                            >
                                Not you? Switch account
                            </button>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Left panel: divisions (step 1) or teams (step 2) */}
                        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-sm">
                            {/* Breadcrumb */}
                            <div className="mb-3 flex items-center gap-1 text-xs">
                                <span className={step === 'divisions' ? 'font-semibold text-[#082F8A]' : 'text-default-400'}>{noDivisions ? 'Registration' : 'Select divisions'}</span>
                                {hasTeamStep && <>
                                    <span className="text-default-300">›</span>
                                    <span className={step === 'teams' ? 'font-semibold text-[#082F8A]' : 'text-default-400'}>{teamSport ? 'Your team' : 'Select teams'}</span>
                                </>}
                                {hasInfo && <>
                                    <span className="text-default-300">›</span>
                                    <span className={step === 'information' ? 'font-semibold text-[#082F8A]' : 'text-default-400'}>Information</span>
                                </>}
                                <span className="text-default-300">›</span>
                                <span className={step === 'checkout' ? 'font-semibold text-[#082F8A]' : 'text-default-400'}>Checkout</span>
                            </div>

                            {step === 'divisions' ? (<>
                                <h2 className="font-bebas text-2xl tracking-wide text-[#1a1a2e]">Registration</h2>
                                <p className="mb-4 text-sm text-default-500">{noDivisions ? (eventFull ? 'This event is full — you can join the waiting list below' : 'Confirm your entry below') : allowMultiple ? 'Select the divisions you want to compete in' : 'Select the division you want to compete in'}</p>

                                {e.dupr_premium_only && (
                                    <div className="mb-3 rounded-xl border border-[#082F8A]/30 bg-[#082F8A]/5 px-4 py-3 text-sm text-[#082F8A]">
                                        <span className="font-semibold">DUPR Premium members only.</span>{' '}
                                        Everyone entering (including partners) needs a ScorePal account with DUPR connected and an
                                        active DUPR Premium (DUPR+) membership. Connect DUPR from your profile before registering.
                                    </div>
                                )}

                                {eventFull && (
                                    <div className="mb-3 rounded-xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700">
                                        <span className="font-semibold">This event is currently full.</span>{' '}
                                        You can still sign up to join the waiting list — the organizer will move you in if a spot opens up.
                                        {(e.waitlist_count ?? 0) > 0 && (
                                            <span className="mt-1 block text-xs">
                                                {e.waitlist_count} {isDoubles ? (e.waitlist_count === 1 ? 'team is' : 'teams are') : (e.waitlist_count === 1 ? 'player is' : 'players are')} already on the waiting list.
                                            </span>
                                        )}
                                    </div>
                                )}

                                {divisions.length === 0 ? (
                                    <p className="text-sm text-default-500">This tournament has no divisions set up.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {divisions.map((d) => {
                                            const isSel = selected.has(d.key);
                                            const fee = d.entry_amount ?? e.entry_amount;
                                            const full = d.capacity != null && d.count >= d.capacity;
                                            // A full EVENT still accepts entries (backend waitlists them), so those cards
                                            // stay selectable as an explicit waitlist opt-in. A full DIVISION on a
                                            // non-full event has no waitlist — those remain disabled as before.
                                            const waitlistable = full && eventFull;
                                            const blocked = full && !eventFull;
                                            return (
                                                <button
                                                    key={d.key}
                                                    type="button"
                                                    disabled={blocked}
                                                    onClick={() => toggle(d.key)}
                                                    className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                                                        isSel ? 'border-[#082F8A] bg-[#082F8A]/5' : 'border-[#e5e7eb] hover:bg-[#F6F7FB]'
                                                    } ${blocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                >
                                                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                                                        isSel ? 'border-[#082F8A] bg-[#082F8A] text-white' : 'border-default-300'
                                                    }`}>
                                                        {isSel && <Check className="h-3.5 w-3.5" />}
                                                    </span>
                                                    <span className="flex-1">
                                                        <span className="block text-sm font-medium text-[#1a1a2e]">{d.name}</span>
                                                        {waitlistable
                                                            ? <span className="block text-xs font-semibold text-warning-600">Full — {isSel ? "you'll be added to the waiting list" : 'select to join the waiting list'}</span>
                                                            : full && <span className="block text-xs text-danger">Full</span>}
                                                    </span>
                                                    <span className="text-sm font-semibold text-[#1a1a2e]">
                                                        {fee ? `${fmt(fee)} / player` : 'Free'}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {allowMultiple && !noDivisions && (
                                    <p className="mt-3 text-xs text-default-500">
                                        <span className="font-semibold text-[#082F8A]">Did you know?</span> You can sign up for multiple divisions at once.
                                    </p>
                                )}

                                <div className="mt-4 flex items-center justify-between border-t border-[#e5e7eb] pt-4">
                                    <div>
                                        <span className="text-xs uppercase tracking-wide text-default-500">Total</span>
                                        <div className="font-bebas text-2xl text-[#1a1a2e]">{fmt(totalCents)}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={onContinue}
                                        disabled={submitting || selected.size === 0}
                                        className="rounded-full bg-[#082F8A] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#06246b] disabled:opacity-60"
                                    >
                                        {eventFull ? 'Join waiting list' : 'Continue'}
                                    </button>
                                </div>
                            </>) : step === 'teams' ? (<>
                                <h2 className="font-bebas text-2xl tracking-wide text-[#1a1a2e]">{addPartnerMode ? 'Add your partner' : teamSport ? 'Your team' : 'Select your team'}</h2>
                                {teamSport ? (
                                    <p className="mb-4 text-sm text-default-500">Name your team — you're its captain. Add your squad now or later.</p>
                                ) : addPartnerMode ? (
                                    <p className="mb-4 rounded-lg bg-[#082F8A]/5 px-3 py-2 text-sm text-[#082F8A]">
                                        You're already registered — just pick your partner below and save. No payment needed.
                                    </p>
                                ) : (
                                    <p className="mb-4 text-sm text-default-500">Name your team and pick a partner for each division</p>
                                )}

                                <div className="space-y-3">
                                    {selectedDivisions.map((d) => teamSport ? (
                                        <TeamSportEntryCard
                                            key={d.key}
                                            division={d}
                                            captain={user?.display_name || user?.name}
                                            value={teams[d.key] || {teamName: '', partner: null}}
                                            onChange={(v) => setTeam(d.key, {...(teams[d.key] || {partner: null}), ...v})}
                                        />
                                    ) : (
                                        <DivisionTeamCard
                                            key={d.key}
                                            division={d}
                                            eventUid={e.uid}
                                            perPlayer={perPlayer}
                                            value={teams[d.key] || {teamName: '', partner: null}}
                                            onChange={(v) => setTeam(d.key, v)}
                                        />
                                    ))}
                                </div>

                                <div className="mt-4 flex items-center justify-between border-t border-[#e5e7eb] pt-4">
                                    <button type="button" onClick={() => setStep('divisions')} className="text-sm font-semibold text-default-500 hover:text-[#1a1a2e]">
                                        ‹ Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onSubmitTeams}
                                        disabled={submitting}
                                        className="rounded-full bg-[#082F8A] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#06246b] disabled:opacity-60"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </>) : step === 'information' ? (<>
                                <h2 className="font-bebas text-2xl tracking-wide text-[#1a1a2e]">Information</h2>
                                <p className="mb-4 text-sm text-default-500">Just a few details before you register</p>

                                {fields.length > 0 && (
                                    <div className="space-y-3">
                                        {fields.map((f) => (
                                            f.type === 'select' ? (
                                                <Select
                                                    key={f.key}
                                                    label={f.label}
                                                    isRequired={!!f.required}
                                                    placeholder="Select an option"
                                                    selectedKeys={formAnswers[f.key] ? [formAnswers[f.key]] : []}
                                                    onSelectionChange={(keys: any) => setFormAnswers({...formAnswers, [f.key]: (keys.currentKey as string) || ''})}
                                                >
                                                    {(f.options || []).map((o: string) => (
                                                        <SelectItem key={o} textValue={o}>{o}</SelectItem>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <Input
                                                    key={f.key}
                                                    label={f.label}
                                                    isRequired={!!f.required}
                                                    value={formAnswers[f.key] || ''}
                                                    onChange={(ev) => setFormAnswers({...formAnswers, [f.key]: ev.target.value})}
                                                />
                                            )
                                        ))}
                                    </div>
                                )}

                                {regPlayers.length > 0 && (
                                    <div className={fields.length > 0 ? 'mt-6' : ''}>
                                        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-default-500">Players Information</h3>
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            {regPlayers.map((p) => (
                                                <div key={p.key} className="rounded-xl border border-[#e5e7eb] p-4">
                                                    <div className="mb-3 flex flex-col items-center text-center">
                                                        {p.image ? (
                                                            <img src={p.image} alt={p.name} className="h-12 w-12 rounded-full object-cover" />
                                                        ) : (
                                                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#082F8A] text-lg font-semibold text-white">
                                                                {p.name.trim().charAt(0).toUpperCase() || '?'}
                                                            </span>
                                                        )}
                                                        <div className="mt-1 text-sm font-medium text-[#1a1a2e]">{p.name}{p.self ? ' (you)' : p.isGuest ? ' (guest)' : ''}</div>
                                                    </div>
                                                    <Input
                                                        size="sm"
                                                        type="email"
                                                        placeholder="Email"
                                                        value={cEmail(p)}
                                                        onChange={(ev) => p.isGuest ? setGuestEmail(p.divKey!, ev.target.value) : setContact(p.key, 'email', ev.target.value)}
                                                        className="mb-2"
                                                    />
                                                    {!p.isGuest && collectPhone && (
                                                        <PPhoneInput
                                                            country={getPhone(p.key, p.self).country}
                                                            number={getPhone(p.key, p.self).national}
                                                            onCountryChange={(iso) => setPhone(p.key, {country: iso}, p.self)}
                                                            onNumberChange={(n) => setPhone(p.key, {national: n}, p.self)}
                                                            label=""
                                                            isRequired={!!p.self}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {waiversList.length > 0 && (
                                    <div className="mt-4 space-y-3">
                                        {waiversList.map((w) => (
                                            <div key={w.key} className="flex items-center gap-2">
                                                <Checkbox size="sm" isSelected={!!waiversAccepted[w.key]}
                                                          onValueChange={(v) => setWaiversAccepted({...waiversAccepted, [w.key]: v})} />
                                                <span className="text-sm text-[#1a1a2e]">
                                                    I have read and agree to{' '}
                                                    {w.text ? (
                                                        <button
                                                            type="button"
                                                            className="font-medium text-[#082F8A] underline hover:text-[#06246b]"
                                                            onClick={() => setOpenWaiver(w)}
                                                        >
                                                            {w.title || 'this waiver'}
                                                        </button>
                                                    ) : (w.title || 'this waiver')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-4 flex items-center justify-between border-t border-[#e5e7eb] pt-4">
                                    <button type="button" onClick={() => setStep(hasTeamStep ? 'teams' : 'divisions')} className="text-sm font-semibold text-default-500 hover:text-[#1a1a2e]">
                                        ‹ Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onSubmitInfo}
                                        disabled={!infoValid}
                                        className="rounded-full bg-[#082F8A] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#06246b] disabled:opacity-60"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </>) : (<>
                                <h2 className="font-bebas text-2xl tracking-wide text-[#1a1a2e]">Registration checkout</h2>
                                <p className="mb-4 text-sm text-default-500">Review your order, then pay securely via Stripe</p>

                                <div className="space-y-2 border-b border-[#e5e7eb] pb-4">
                                    {selectedDivisions.map((d) => (
                                        <div key={d.key} className="flex items-center justify-between text-sm">
                                            <span className="text-[#1a1a2e]">{noDivisions ? 'Registration' : `Registration in ${d.name}`}</span>
                                            <span className="font-medium text-[#1a1a2e]">{fmt(d.entry_amount ?? e.entry_amount)}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Only the fee lines the registrant actually funds — under a split
                                    the organizer absorbs the other part, and showing it here would
                                    read as being charged for it. Absorb-everything shows nothing. */}
                                {passFees && feesTotalCents > 0 && (
                                    <div className="mt-4 rounded-xl bg-[#F6F7FB] p-4">
                                        <div className="flex items-center justify-between text-sm font-medium text-[#1a1a2e]">
                                            <span>{feeInfo?.all_in ? 'Booking fee' : 'Processing fees'} <span className="text-xs font-normal text-default-400">(added to your total)</span></span>
                                            <span>{fmt(feesTotalCents)}</span>
                                        </div>
                                        {/* Payments v3: one all-in booking fee per registration — no parts to itemise. */}
                                        {!feeInfo?.all_in && <div className="mt-2 space-y-1 text-xs text-default-500">
                                            {registrantPaysPercent && (
                                                <div className="flex items-center justify-between">
                                                    <span>{feeRate}% processing fee</span>
                                                    <span>{fmt(processingFeeCents)}</span>
                                                </div>
                                            )}
                                            {registrantPaysFixed && (
                                                <div className="flex items-center justify-between">
                                                    <span>{fmt(feeFixed)} / division / player<sup>1</sup></span>
                                                    <span>{fmt(fixedFeeCents)}</span>
                                                </div>
                                            )}
                                            {registrantPaysFixed && (
                                                <p className="pt-1 text-[11px] text-default-400"><sup>1</sup> {playersPerTeam} player{playersPerTeam > 1 ? 's' : ''} per team across {selected.size} division{selected.size > 1 ? 's' : ''}.</p>
                                            )}
                                        </div>}
                                    </div>
                                )}

                                <div className="mt-4 flex items-center justify-between border-t border-[#e5e7eb] pt-4">
                                    <span className="text-sm font-semibold uppercase tracking-wide text-default-500">Total</span>
                                    <span className="font-bebas text-2xl text-[#1a1a2e]">{fmt(payableTotalCents)}</span>
                                </div>

                                <div className="mt-4 rounded-xl border border-[#e5e7eb] bg-[#F6F7FB] p-3 text-xs text-default-500">
                                    {totalCents > 0 && e.payments_ready
                                        ? "You'll be redirected to Stripe to pay securely. We never store your card details."
                                        : totalCents > 0
                                            ? "Your spot will be reserved now — the organizer will send a payment link once payments are enabled."
                                            : 'This entry is free — no payment required.'}
                                </div>

                                <div className="mt-4 flex items-center justify-between border-t border-[#e5e7eb] pt-4">
                                    <button type="button" onClick={() => setStep(hasInfo ? 'information' : (hasTeamStep ? 'teams' : 'divisions'))} className="text-sm font-semibold text-default-500 hover:text-[#1a1a2e]">
                                        ‹ Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={submit}
                                        disabled={submitting}
                                        className="rounded-full bg-[#082F8A] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#06246b] disabled:opacity-60"
                                    >
                                        {submitting
                                            ? 'Please wait…'
                                            : (totalCents > 0 && e.payments_ready ? 'Submit payment' : 'Complete registration')}
                                    </button>
                                </div>
                            </>)}
                        </div>

                        {/* Teams grouped by division */}
                        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-sm">
                            <h2 className="font-bebas text-2xl tracking-wide text-[#1a1a2e]">{hasTeamStep ? 'Teams' : 'Players'}</h2>
                            <p className="mb-4 text-xs text-default-500">{rosterSummary(e, {teamSport, isDoubles})}</p>
                            {noDivisions ? (
                                (e.registered && e.registered.length > 0) ? (
                                    <div className="space-y-1">
                                        {(e.registered as string[]).map((n, i) => (
                                            <div key={i} className="rounded-lg bg-[#F6F7FB] px-3 py-2 text-sm font-medium text-[#1a1a2e]">{n}</div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-default-500">No registrations yet.</p>
                                )
                            ) : divisions.length === 0 ? (
                                <p className="text-sm text-default-500">No registrations yet.</p>
                            ) : (
                                <div className="space-y-4">
                                    {divisions.map((d) => (
                                        <div key={d.key}>
                                            <div className="mb-1.5 flex items-center justify-between">
                                                <span className="text-sm font-semibold text-[#1a1a2e]">{d.name}</span>
                                                <span className="text-xs text-default-400">({d.count})</span>
                                            </div>
                                            {d.teams.length === 0 ? (
                                                <p className="text-xs text-default-400">No teams yet.</p>
                                            ) : (
                                                <div className="space-y-1">
                                                    {d.teams.map((t, i) => (
                                                        <div key={i} className="rounded-lg bg-[#F6F7FB] px-3 py-2">
                                                            <span className="block text-sm font-medium text-[#1a1a2e]">{t.name}</span>
                                                            {t.players.length > 0 && (
                                                                <span className="block text-xs text-default-500">{t.players.join(' and ')}</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Waiting list — entries queued while the event is full */}
                            {(e.waitlist_count ?? 0) > 0 && (
                                <div className="mt-4 border-t border-[#e5e7eb] pt-3">
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <span className="text-sm font-semibold text-[#1a1a2e]">Waiting list</span>
                                        <span className="text-xs text-default-400">({e.waitlist_count})</span>
                                    </div>
                                    <div className="space-y-1">
                                        {((e.waitlisted as string[]) || []).map((n, i) => (
                                            <div key={i} className="rounded-lg bg-warning-50 px-3 py-2 text-sm font-medium text-warning-700">{n}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                  </div>

                  {/* Side ad rail (large screens only). Uses the dedicated registration ad unit
                      (config.adsenseRegistrationSlot — set VITE_ADSENSE_REGISTRATION_SLOT). */}
                  {config.adsenseEnabled && (
                    <aside className="hidden w-[300px] shrink-0 lg:block">
                        <div className="sticky top-24">
                            <div className="mb-1 text-center text-[10px] uppercase tracking-wider text-white/40">Advertisement</div>
                            <AdSensePush>
                                <ins
                                    className="adsbygoogle"
                                    style={{display: 'block', minHeight: 600}}
                                    data-ad-client={config.adsenseClient}
                                    data-ad-slot={config.adsenseRegistrationSlot}
                                    data-ad-format="auto"
                                    data-full-width-responsive="true"
                                />
                            </AdSensePush>
                        </div>
                    </aside>
                  )}
                </div>
            )}

            {phoneVerifyOpen && (
                <VerifyPhoneModal
                    reason="You're registered! Verify your mobile so you can report scores by WhatsApp for this event."
                    initialPhone={user?.uid ? cPhone({key: user.uid, name: '', self: true} as any) : undefined}
                    initialCountry={user?.uid ? getPhone(user.uid, true).country : undefined}
                    onClose={() => { setPhoneVerifyOpen(false); goToEvent(); }}
                />
            )}

            {/* Pre-enrolment verification gate (organizer collects phone): registration is only
                submitted after the code checks out; dismissing leaves nothing registered. */}
            {preVerifyOpen && (
                <VerifyPhoneModal
                    reason="Verify your mobile to complete your registration — the organizer requires a verified number for this event."
                    initialPhone={user?.uid ? cPhone({key: user.uid, name: '', self: true} as any) : undefined}
                    initialCountry={user?.uid ? getPhone(user.uid, true).country : undefined}
                    onVerified={() => { preVerifySucceeded.current = true; phoneJustVerified.current = true; }}
                    onClose={() => {
                        setPreVerifyOpen(false);
                        if (preVerifySucceeded.current) {
                            void submit();
                        } else {
                            addToast({color: 'warning', title: 'Registration not completed — verify your mobile to finish signing up.'});
                        }
                    }}
                />
            )}

            <Modal isOpen={rulesOpen} onOpenChange={(o) => !o && setRulesOpen(false)} scrollBehavior="inside" size="2xl">
                <ModalContent>
                    <ModalHeader>Rules</ModalHeader>
                    <ModalBody className="pb-6">
                        <div className="whitespace-pre-wrap text-sm text-default-600">{e?.rules}</div>
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={!!openWaiver} onOpenChange={(o) => !o && setOpenWaiver(null)} scrollBehavior="inside" size="2xl">
                <ModalContent>
                    <ModalHeader>{openWaiver?.title || 'Waiver'}</ModalHeader>
                    <ModalBody className="pb-6">
                        <div className="whitespace-pre-wrap text-sm text-default-600">{openWaiver?.text}</div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </main>
    );
}

function DivisionTeamCard({division, eventUid, perPlayer, value, onChange}: {
    division: DivisionWithTeams;
    eventUid: string;
    perPlayer: boolean;
    value: TeamEntry;
    onChange: (v: TeamEntry) => void;
}) {
    const [mode, setMode] = useState<'search' | 'manual'>('search');
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<{uid: string; name: string; image?: string}[] | null>(null);
    const [searching, setSearching] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');

    const partnerMode: PartnerMode = value.partnerMode || 'partner';

    const runSearch = async () => {
        if (!search.trim()) return;
        setSearching(true);
        try {
            const res = await services.players.search({searchText: search.trim(), event_partner_tournament_uid: eventUid});
            setResults((Array.isArray(res) ? res : []).map((p: any) => ({
                uid:   p.uid,
                name:  p.display_name || (p.name + (p.lastname ? ' ' + p.lastname : '')),
                image: p.profile_thumb_url || p.profile_image_url || undefined,
            })));
        } finally {
            setSearching(false);
        }
    };

    const addManual = () => {
        if (!firstName.trim()) {
            addToast({color: 'warning', title: 'Partner first name is required'});
            return;
        }
        // Store the guest partner locally (no uid); the player is created server-side on submit.
        const name = `${firstName.trim()} ${lastName.trim()}`.trim();
        onChange({...value, partner: {name, email: email.trim() || undefined}});
    };

    return (
        <div className="rounded-xl border border-[#e5e7eb] p-4">
            <div className="mb-2 text-sm font-semibold text-[#1a1a2e]">{division.name}</div>
            <input
                className="mb-3 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                placeholder="Team name (optional)"
                value={value.teamName}
                onChange={(ev) => onChange({...value, teamName: ev.target.value})}
            />
            <div className="mb-3 grid grid-cols-3 gap-1.5">
                {(([['partner', 'I have a partner'], ['later', 'Choose later'], ['looking', 'Find me a partner']]) as [PartnerMode, string][]).map(([m, label]) => (
                    <button
                        key={m}
                        type="button"
                        onClick={() => onChange({...value, partnerMode: m, partner: m === 'partner' ? value.partner : null})}
                        className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition ${partnerMode === m ? 'border-[#082F8A] bg-[#082F8A]/5 text-[#082F8A]' : 'border-[#e5e7eb] text-default-500 hover:border-[#082F8A]/40'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {partnerMode === 'later' ? (
                <p className="rounded-lg bg-[#F6F7FB] px-3 py-2 text-xs text-default-500">You'll register without a partner for now — you can add one to your team later.</p>
            ) : partnerMode === 'looking' ? (
                <p className="rounded-lg bg-[#F6F7FB] px-3 py-2 text-xs text-default-500">We'll add you to this event's "looking for a partner" list so other players can team up with you.</p>
            ) : value.partner ? (
                <div className="space-y-2">
                    <div className="flex items-center justify-between rounded-lg bg-[#082F8A]/5 px-3 py-2">
                        <span className="flex items-center gap-2 text-sm text-[#1a1a2e]">
                            <PartnerAvatar name={value.partner.name} image={value.partner.image} />
                            <span><span className="text-default-500">Partner:</span> {value.partner.name}</span>
                        </span>
                        <button type="button" className="text-xs font-semibold text-[#082F8A]" onClick={() => onChange({...value, partner: null})}>Change</button>
                    </div>
                    {perPlayer && (
                        <div className="space-y-2 rounded-lg border border-[#e5e7eb] p-2.5">
                            <p className="text-xs text-default-500">This event charges per player — we'll email {value.partner.name.split(' ')[0] || 'your partner'} a link to pay their own entry fee.</p>
                            <input
                                className="w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                                type="email"
                                placeholder="Partner email *"
                                value={value.partner.email || ''}
                                onChange={(ev) => onChange({...value, partner: {...value.partner!, email: ev.target.value}})}
                            />
                            <input
                                className="w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                                type="tel"
                                placeholder="Partner phone *"
                                value={value.partner.phone || ''}
                                onChange={(ev) => onChange({...value, partner: {...value.partner!, phone: ev.target.value}})}
                            />
                        </div>
                    )}
                </div>
            ) : mode === 'search' ? (
                <div>
                    <div className="flex gap-2">
                        <input
                            className="flex-1 rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                            placeholder="Search partner by name"
                            value={search}
                            onChange={(ev) => setSearch(ev.target.value)}
                            onKeyDown={(ev) => ev.key === 'Enter' && runSearch()}
                        />
                        <button type="button" onClick={runSearch} disabled={searching}
                                className="rounded-lg bg-[#082F8A] px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                            {searching ? '…' : 'Search'}
                        </button>
                    </div>
                    {results && (
                        <div className="mt-2 max-h-40 overflow-auto rounded-lg border border-[#e5e7eb]">
                            {results.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-default-500">No players found.</p>
                            ) : results.map((p) => (
                                <button key={p.uid} type="button" onClick={() => {onChange({...value, partner: p}); setResults(null);}}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#1a1a2e] hover:bg-[#F6F7FB]">
                                    <PartnerAvatar name={p.name} image={p.image} />
                                    <span>{p.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <button type="button" onClick={() => setMode('manual')}
                            className="mt-2 text-xs font-semibold text-[#082F8A] hover:underline">
                        Can't find them? Add a partner manually
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <input
                            className="flex-1 rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                            placeholder="First name"
                            value={firstName}
                            onChange={(ev) => setFirstName(ev.target.value)}
                        />
                        <input
                            className="flex-1 rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                            placeholder="Last name (optional)"
                            value={lastName}
                            onChange={(ev) => setLastName(ev.target.value)}
                        />
                    </div>
                    <input
                        className="w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm text-[#1a1a2e] outline-none focus:border-[#082F8A]"
                        placeholder="Email (optional)"
                        type="email"
                        value={email}
                        onChange={(ev) => setEmail(ev.target.value)}
                    />
                    <div className="flex items-center justify-between">
                        <button type="button" onClick={() => setMode('search')}
                                className="text-xs font-semibold text-default-500 hover:text-[#1a1a2e]">
                            ‹ Back to search
                        </button>
                        <button type="button" onClick={addManual}
                                className="rounded-lg bg-[#082F8A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                            Add partner
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
